import { Button as MuiButton, Grid, Tooltip, Typography } from '@mui/material'
import { FormikHelpers } from 'formik'
import { inject, observer } from 'mobx-react'
import moment from 'moment'
import * as React from 'react'
import { useState, useEffect, useCallback, useRef, useMemo } from 'react'
import FullWidthField from 'src/common/FullWidthField'
import SelectField from 'src/common/SelectField/SelectField'
import { noWhitespaceString } from 'src/common/YupExtensions'
import { IProviderDto } from 'src/generated_client'
import {
  createIScheduleFormValues,
  IScheduleFormValues,
  toISchedule,
} from 'src/patients/Scheduling/ScheduleFormValues'
import ReceiveClaimProviderLookupDialog from './ReceiveClaimProviderLookupDialog'
import * as Yup from 'yup'
import ESDialog from '../common/ESDialog'
import {
  IComponent,
  IEpisodeOfCare,
  ILocation,
  IOrganization,
  IPatient,
  IProvider,
  ISchedule,
} from '../Definitions'
import { IStores } from '../Stores'
import { InfoOutlined, Schedule } from '@mui/icons-material'
import { getHistoricalProvidersSummary } from '../ReceiveClaim/SimpleEventDialogService'
import { HistoricalProvidersSummary } from './HistoricalProvidersSummary'
import { checkComponentDuplicates } from '../services/LookupService'
import { DuplicateClaimSummary } from '../viewModels/workflow/DuplicateClaimSummary'
import _ from 'lodash'

const maximumDate = moment().subtract(1, 'days')

const SimpleEventSchema = Yup.object().shape({
  appointmentDateInput: Yup.date()
    .max(maximumDate.toLocaleString(), 'Appointment Date must be in the past.')
    .required('Appointment Date is required'),
  componentId: noWhitespaceString('Component is required', true),
  locationId: noWhitespaceString('Location is required', true),
  providerId: noWhitespaceString('Provider is required', true),
})

interface ISimpleEventDialogProps {
  clearLocations?: () => void
  closeModal?: () => void
  components?: IComponent[]
  componentId?: IComponent
  currentAppOrganization?: IOrganization
  getAllActiveComponents?: () => Promise<void>
  episodeOfCare?: IEpisodeOfCare
  getAllLocationsForProvider?: (providerId: string) => Promise<void>
  getProviders?: (filter: string) => Promise<void>
  getScheduleById?: (id: string) => Promise<void>
  isLoading?: boolean
  isOpen?: boolean
  isProviderLookupDialogOpen?: boolean
  locations?: ILocation[]
  openProviderLookupDialog?: () => void
  patient?: IPatient
  providers?: IProviderDto[]
  saveSchedule?: (
    schedule: ISchedule,
    eocId: string,
    scheduleId: string,
    resetUiState: boolean
  ) => Promise<string | void>
  schedule?: ISchedule
  selectedLocation?: ILocation
  selectedProvider?: IProvider
  setSelectedLocation?: (location?: ILocation) => void
  setSelectedProvider?: (provider?: IProvider) => Promise<void>
  updateParentStateAfterModal?: (
    locationId: string | undefined,
    scheduleId?: string
  ) => void
  updateParentStateAfterClose?: () => void
  getLocationById?: (locationId: string) => void
  historicalProviders?: HistoricalProvidersSummary[]
}

const SimpleEventDialog: React.FC<ISimpleEventDialogProps> = observer((props) => {
  const {
    clearLocations,
    closeModal,
    components,
    currentAppOrganization,
    getAllActiveComponents,
    episodeOfCare,
    getAllLocationsForProvider,
    getProviders,
    getScheduleById,
    isLoading,
    isOpen,
    isProviderLookupDialogOpen,
    locations,
    openProviderLookupDialog,
    providers,
    saveSchedule,
    schedule,
    selectedLocation,
    selectedProvider,
    setSelectedLocation,
    setSelectedProvider,
    updateParentStateAfterModal,
    updateParentStateAfterClose,
    getLocationById,
  } = props

  const [historicalProviders, setHistoricalProviders] = useState<
    HistoricalProvidersSummary[]
  >([])
  const [duplicateWarning, setDuplicateWarning] = useState<DuplicateClaimSummary | null>(
    null
  )
  const [proceedWithDuplicate, setProceedWithDuplicate] = useState(false)
  const [saveButtonText, setSaveButtonText] = useState('Check for Duplicates')
  const [noDuplicatesFound, setNoDuplicatesFound] = useState(false)
  const [isSaveDisabled, setIsSaveDisabled] = useState(true)
  const [lastCheckedValues, setLastCheckedValues] = useState<
    | {
        componentId?: string
        providerId?: string
        locationId?: string
        appointmentDate?: string
      }
    | undefined
  >(undefined)

  const debouncedFetchProvidersRef = useRef(
    _.debounce(async (componentId: string) => {
      const eocId = episodeOfCare?.id
      if (eocId && componentId) {
        const historicalData = await getHistoricalProvidersSummary(eocId, componentId)
        setHistoricalProviders(historicalData)
      }
    }, 300)
  )

  useEffect(() => {
    return () => {
      debouncedFetchProvidersRef.current.cancel()
    }
  }, [])

  const updateSaveButtonText = useCallback(() => {
    setSaveButtonText('Check for Duplicates')
    setNoDuplicatesFound(false)
    setDuplicateWarning(null)
    setLastCheckedValues(undefined)
    setProceedWithDuplicate(false)
    setIsSaveDisabled(true)
  }, [components])

  const checkIfValuesChanged = useCallback(
    (values: IScheduleFormValues): boolean => {
      if (!lastCheckedValues) return false

      const currentDate = values.appointmentDateInput
        ? moment(values.appointmentDateInput).format('YYYY-MM-DD')
        : values.appointmentDateInput
      const savedDate = lastCheckedValues.appointmentDate
        ? moment(lastCheckedValues.appointmentDate).format('YYYY-MM-DD')
        : lastCheckedValues.appointmentDate

      return (
        lastCheckedValues.componentId !== values.componentId ||
        lastCheckedValues.providerId !== selectedProvider?.id ||
        lastCheckedValues.locationId !== selectedLocation?.id ||
        savedDate !== currentDate
      )
    },
    [lastCheckedValues, selectedProvider, selectedLocation]
  )

  const resetDuplicateCheckState = useCallback(() => {
    setNoDuplicatesFound(false)
    setDuplicateWarning(null)
    setSaveButtonText('Check for Duplicates')
    setLastCheckedValues(undefined)
    setProceedWithDuplicate(false)
    setIsSaveDisabled(true)
  }, [components])

  const actualSave = useCallback(
    async (values: IScheduleFormValues) => {
      try {
        values.locationId = selectedLocation?.id ?? ''
        const scheduleData = await toISchedule(values, schedule)
        scheduleData.patientId = episodeOfCare?.referral?.patient?.id ?? ''
        scheduleData.episodeOfCareId = episodeOfCare!.id
        scheduleData.isTBD = false
        if (!scheduleData.episodeOfCare) {
          scheduleData.isNew = true
        }

        scheduleData.appointmentDate = new Date(
          moment(values.appointmentDateInput, 'YYYY-MM-DD').toISOString()
        )

        const scheduleId = await saveSchedule!(
          scheduleData,
          episodeOfCare!.id,
          scheduleData.id || '',
          false
        )

        if (updateParentStateAfterModal && typeof scheduleId === 'string' && scheduleId) {
          await getScheduleById!(scheduleId)
          if (selectedLocation?.id) {
            await getLocationById!(selectedLocation.id)
            updateParentStateAfterModal!(selectedLocation.id, scheduleId)
          }
        }

        setDuplicateWarning(null)
        setProceedWithDuplicate(false)
        closeModal!()
      } catch (error) {
        console.error(
          'Error saving schedule:',
          error instanceof Error ? error.message : 'Unknown error occurred'
        )
      }
    },
    [
      selectedLocation,
      schedule,
      episodeOfCare,
      saveSchedule,
      updateParentStateAfterModal,
      getScheduleById,
      getLocationById,
      closeModal,
    ]
  )

  const checkForDuplicates = useCallback(
    async (values: IScheduleFormValues): Promise<boolean> => {
      try {
        if (
          !values.componentId ||
          !selectedProvider?.id ||
          !selectedLocation?.id ||
          !values.appointmentDateInput ||
          !episodeOfCare?.id
        ) {
          return false
        }

        const request = {
          componentId: values.componentId,
          providerId: selectedProvider.id,
          locationId: selectedLocation.id,
          appointmentDate: new Date(
            moment(values.appointmentDateInput, 'YYYY-MM-DD HH:mm').toISOString()
          ),
          episodeOfCareId: episodeOfCare.id,
        }

        const duplicates = await checkComponentDuplicates(request)

        if (duplicates) {
          setDuplicateWarning(duplicates)
          return true
        }

        return false
      } catch (error) {
        console.error('Error checking for duplicates:', error)
        return false
      }
    },
    [selectedProvider, selectedLocation, episodeOfCare]
  )

  const save = useCallback(
    async (
      values: IScheduleFormValues,
      formikBag: FormikHelpers<IScheduleFormValues>
    ) => {
      try {
        if (duplicateWarning && proceedWithDuplicate) {
          await actualSave(values)
          formikBag.setSubmitting(false)
          return
        }

        if (noDuplicatesFound) {
          await actualSave(values)
          formikBag.setSubmitting(false)
          return
        }

        const duplicateCheck = await checkForDuplicates(values)

        if (duplicateCheck) {
          setProceedWithDuplicate(true)
          setSaveButtonText('Save Anyway')
          setIsSaveDisabled(false)
          setLastCheckedValues({
            componentId: values.componentId,
            providerId: selectedProvider?.id,
            locationId: selectedLocation?.id,
            appointmentDate: values.appointmentDateInput,
          })
          formikBag.setSubmitting(false)
          return
        }

        setNoDuplicatesFound(true)
        setSaveButtonText('Save')
        setIsSaveDisabled(false)
        setLastCheckedValues({
          componentId: values.componentId,
          providerId: selectedProvider?.id,
          locationId: selectedLocation?.id,
          appointmentDate: values.appointmentDateInput,
        })
        formikBag.setSubmitting(false)
      } catch (error) {
        console.error(
          'Error checking for duplicates:',
          error instanceof Error ? error.message : 'Unknown error occurred'
        )
      } finally {
        formikBag.setSubmitting(false)
      }
    },
    [
      components,
      duplicateWarning,
      proceedWithDuplicate,
      noDuplicatesFound,
      actualSave,
      checkForDuplicates,
      selectedProvider,
      selectedLocation,
    ]
  )

  const close = useCallback(() => {
    setDuplicateWarning(null)
    setProceedWithDuplicate(false)
    setNoDuplicatesFound(false)
    setLastCheckedValues(undefined)
    setIsSaveDisabled(true)
    if (updateParentStateAfterClose) {
      updateParentStateAfterClose()
    }
    closeModal!()
  }, [updateParentStateAfterClose, closeModal])

  const setup = useCallback(async () => {
    setHistoricalProviders([])
    setDuplicateWarning(null)
    setProceedWithDuplicate(false)
    setNoDuplicatesFound(false)
    setSaveButtonText('Check for Duplicates')
    setLastCheckedValues(undefined)
    setIsSaveDisabled(true)
    setSelectedLocation!(undefined)
    setSelectedProvider!(undefined)
    await getAllActiveComponents!()
  }, [getAllActiveComponents, setSelectedLocation, setSelectedProvider])

  useEffect(() => {
    if (isOpen) {
      setup()
      if (schedule?.componentId) {
        updateSaveButtonText()
      }
    }
  }, [isOpen])

  useEffect(() => {
    resetDuplicateCheckState()
  }, [selectedProvider, selectedLocation, resetDuplicateCheckState])

  useEffect(() => {
    if (!isOpen) {
      const timer = setTimeout(() => {
        setHistoricalProviders([])
        setDuplicateWarning(null)
        setProceedWithDuplicate(false)
        setNoDuplicatesFound(false)
        setLastCheckedValues(undefined)
        setIsSaveDisabled(true)
        setSelectedLocation!(undefined)
        setSelectedProvider!(undefined)
        clearLocations!()
      }, 300)
      return () => clearTimeout(timer)
    } else {
      setHistoricalProviders([])
      setDuplicateWarning(null)
      setProceedWithDuplicate(false)
      setNoDuplicatesFound(false)
      setLastCheckedValues(undefined)
      setIsSaveDisabled(true)
      return undefined
    }
  }, [isOpen, setSelectedLocation, setSelectedProvider, clearLocations])

  const flattenedProviders = useMemo(
    () => historicalProviders.flat(),
    [historicalProviders]
  )

  const dropdownComponents = useMemo(
    () =>
      components?.filter(
        (x) => x.organizationId === currentAppOrganization!.id && x.isSimpleComponent
      ),
    [components, currentAppOrganization]
  )

  const getProviderName = (provider: IProvider) => {
    return provider !== null ? provider.name : ''
  }

  const getProviderId = (provider: IProvider) => {
    return provider !== null ? provider.id || '' : ''
  }

  const openProviderLookupDialogCallback = useCallback(
    (setFieldValue: (field: string, value: any) => void, values: IScheduleFormValues) =>
      () => {
        if (values.providerId) {
          setFieldValue('providerId', undefined)
        }
        if (values.locationId) {
          setFieldValue('locationId', undefined)
        }
        openProviderLookupDialog!()
      },
    [openProviderLookupDialog]
  )

  const getName = (item: any) => {
    return item ? item.name || '' : ''
  }

  const getValue = (item: any) => {
    return item ? item.id || '' : ''
  }

  const setProviderValue = useCallback(
    (setFieldValue: (field: string, value: any) => void, values: IScheduleFormValues) =>
      (fieldName: string, value: any) => {
        const newValue = value !== undefined ? value.id : ''
        setFieldValue(`${fieldName}Id`, newValue)
        setFieldValue(fieldName, value)
        setSelectedProvider!(value).then(() => {
          getAllLocationsForProvider!(newValue)
        })

        if (!!newValue) {
          if (newValue !== values.providerId) {
            setFieldValue('locationId', undefined)
            setSelectedLocation!(undefined)
            clearLocations!()
            setSelectedProvider!(value)
            getAllLocationsForProvider!(newValue).then(() => {
              if (locations!.length === 1) {
                setFieldValue('locationId', locations![0].name)
                setSelectedLocation!(locations![0])
              }
            })
          }
        } else {
          setSelectedProvider!(undefined)
          setSelectedLocation!(undefined)
          clearLocations!()
          setFieldValue('locationId', undefined)
        }
      },
    [
      setSelectedProvider,
      getAllLocationsForProvider,
      setSelectedLocation,
      clearLocations,
      locations,
    ]
  )

  const getSelectedProvider = useCallback(
    (values: IScheduleFormValues, setFieldValue: (field: string, value: any) => void) => {
      if (selectedProvider && selectedProvider.id !== values.providerId) {
        setFieldValue('providerId', selectedProvider.id)
        setFieldValue('provider', selectedProvider.name)
      }
      if (selectedProvider) {
        if (selectedLocation && selectedLocation.name !== values.locationId) {
          setFieldValue('locationId', selectedLocation.name)
        }
        return selectedProvider.name
      }
      return undefined
    },
    [selectedProvider, selectedLocation]
  )

  const renderInlineDuplicateWarning = () => {
    if (!duplicateWarning) return null

    return (
      <Grid container spacing={2} style={{ marginTop: '20px' }}>
        <Grid item xs={12}>
          <Typography>
            {duplicateWarning.duplicateClaimCount === 1 ? (
              <>
                <strong>A claim</strong> already exists that matches the Provider Group,
                Date of Service, and EOC Number on this claim. Click Save Anyway to
                continue saving this claim or click Close to cancel.
              </>
            ) : (
              <>
                <strong>{duplicateWarning.duplicateClaimCount} claims</strong> already
                exist that match the Provider Group, Date of Service, and EOC Number on
                this claim. Click Save Anyway to continue saving this claim or click Close
                to cancel.
              </>
            )}
          </Typography>
        </Grid>

        <Grid container direction="row" item xs={12}>
          {/* Previous Claim(s) Column */}
          <Grid container direction="column" spacing={1} xs={6}>
            <h1>
              Previous {duplicateWarning.duplicateClaimCount === 1 ? 'Claim' : 'Claims'}
            </h1>
            <Grid item>
              <Grid container direction="row" spacing={2}>
                <Grid item>
                  <strong>Patient Name:</strong>
                </Grid>
                <Grid item>
                  <span>{duplicateWarning.patientName}</span>
                </Grid>
              </Grid>
            </Grid>
            <Grid item>
              <Grid container direction="row" spacing={2}>
                <Grid item>
                  <strong>EOC/Legacy:</strong>
                </Grid>
                <Grid item xs={9}>
                  <span>{duplicateWarning.duplicateEOCLegacyGroup}</span>
                </Grid>
              </Grid>
            </Grid>
            <Grid item>
              <Grid container direction="row" spacing={2}>
                <Grid item>
                  <strong>Body Part:</strong>
                </Grid>
                <Grid item>
                  <span>{duplicateWarning.bodyPart}</span>
                </Grid>
              </Grid>
            </Grid>
            <Grid item>
              <Grid container direction="row" spacing={2}>
                <Grid item>
                  <strong>Provider Group:</strong>
                </Grid>
                <Grid item>
                  <span>{duplicateWarning.providerGroup}</span>
                </Grid>
              </Grid>
            </Grid>
            <Grid item>
              <Grid container direction="row" spacing={2}>
                <Grid item>
                  <strong>Provider Type:</strong>
                </Grid>
                <Grid item>
                  <span>{duplicateWarning.providerTypeList}</span>
                </Grid>
              </Grid>
            </Grid>
            <Grid item>
              <Grid container direction="row" spacing={2}>
                <Grid item>
                  <strong>Date of Service:</strong>
                </Grid>
                <Grid item>
                  <span>
                    {duplicateWarning.dateOfService
                      ? moment(duplicateWarning.dateOfService).format('MM/DD/YYYY')
                      : 'N/A'}
                  </span>
                </Grid>
              </Grid>
            </Grid>
          </Grid>

          {/* Current Claim Column */}
          <Grid container direction="column" spacing={1} xs={6}>
            <h1>Current Claim</h1>
            <Grid item>
              <Grid container direction="row" spacing={2}>
                <Grid item>
                  <strong>Patient Name:</strong>
                </Grid>
                <Grid item>
                  <span>
                    {episodeOfCare?.referral?.patient
                      ? `${episodeOfCare.referral.patient.firstName} ${episodeOfCare.referral.patient.lastName}`
                      : 'N/A'}
                  </span>
                </Grid>
              </Grid>
            </Grid>
            <Grid item>
              <Grid container direction="row" spacing={2}>
                <Grid item>
                  <strong>EOC/Legacy:</strong>
                </Grid>
                <Grid item>
                  <span>{episodeOfCare?.episodeOfCareNumberOrLegacyId || 'N/A'}</span>
                </Grid>
              </Grid>
            </Grid>
            <Grid item>
              <Grid container direction="row" spacing={2}>
                <Grid item>
                  <strong>Body Part:</strong>
                </Grid>
                <Grid item>
                  <span>{episodeOfCare?.bodyPartText || 'N/A'}</span>
                </Grid>
              </Grid>
            </Grid>
            <Grid item>
              <Grid container direction="row" spacing={2}>
                <Grid item>
                  <strong>Provider Group:</strong>
                </Grid>
                <Grid item>
                  <span>{selectedProvider?.name || 'N/A'}</span>
                </Grid>
              </Grid>
            </Grid>
            <Grid item>
              <Grid container direction="row" spacing={2}>
                <Grid item>
                  <strong>Provider Type:</strong>
                </Grid>
                <Grid item>
                  <span>
                    {selectedProvider?.providerTypes?.map((pt) => pt.name).join(', ') ||
                      'N/A'}
                  </span>
                </Grid>
              </Grid>
            </Grid>
            <Grid item>
              <Grid container direction="row" spacing={2}>
                <Grid item>
                  <strong>Date of Service:</strong>
                </Grid>
                <Grid item>
                  <span>
                    {lastCheckedValues?.appointmentDate
                      ? moment(lastCheckedValues.appointmentDate).format('MM/DD/YYYY')
                      : 'N/A'}
                  </span>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    )
  }

  return (
    <>
      {isProviderLookupDialogOpen && (
        <ReceiveClaimProviderLookupDialog
          open={isProviderLookupDialogOpen!}
          selectedSchedule={schedule}
          selectedLocation={selectedLocation}
          setSelectedLocation={setSelectedLocation}
          selectedProvider={selectedProvider}
          setSelectedProvider={setSelectedProvider}
        />
      )}
      <ESDialog
        close={close}
        initialValues={createIScheduleFormValues(schedule)}
        isLoading={isLoading}
        open={isOpen!}
        save={save}
        title={'Add Historical Event'}
        isInitialValid={schedule && !schedule.isNew}
        validationSchema={SimpleEventSchema}
        titleIcon={<Schedule />}
        saveButtonText={saveButtonText}
        disableSaveButton={
          saveButtonText === 'Check for Duplicates' || saveButtonText === 'Save'
            ? undefined
            : isSaveDisabled
        }
      >
        {({ values, setFieldValue, errors }) => {
          if (checkIfValuesChanged(values)) {
            setTimeout(() => resetDuplicateCheckState(), 0)
          }

          return (
            <>
              <Grid container spacing={2} alignItems="center">
                <Grid item lg={3} md={6} sm={6} xs={12} className="grid-item">
                  <SelectField
                    inputId="componentId"
                    getName={getName}
                    getValue={getValue}
                    items={dropdownComponents}
                    label="Component"
                    name="componentId"
                    fullWidth
                    shrinkLabel={true}
                    required
                    errorMessage={errors.componentId}
                    outlined
                    onChange={(event) => {
                      const newComponentId = event?.target?.value || event
                      if (newComponentId) {
                        debouncedFetchProvidersRef.current(newComponentId)
                        updateSaveButtonText()
                        setSelectedProvider!(undefined)
                        setSelectedLocation!(undefined)
                        clearLocations!()
                        setFieldValue('provider', '')
                        setFieldValue('locationId', '')
                        setFieldValue('appointmentDateInput', '')
                      }
                    }}
                  />
                </Grid>

                <Grid item lg={3} md={6} sm={6} xs={12}>
                  <FullWidthField
                    name="provider"
                    inputId="provider"
                    label="Provider Group"
                    placeholder="Select a provider..."
                    outlined
                    fullWidth
                    setFieldValue={setProviderValue(setFieldValue, values)}
                    errorMessage={errors.providerId}
                    getName={getProviderName}
                    getValue={getProviderId}
                    items={providers}
                    getItems={getProviders}
                    selectedValue={getSelectedProvider(values, setFieldValue)}
                    required
                    labelShrink={true}
                    disabled={true}
                  />
                </Grid>

                <Grid item lg={3} md={6} sm={6} xs={12}>
                  <FullWidthField
                    inputId="locationId"
                    label="Provider Location"
                    name="locationId"
                    fullWidth
                    placeholder="Select a location..."
                    shrinkLabel={true}
                    disabled={true}
                    outlined
                    required
                  />
                </Grid>

                <Grid item lg={3} md={6} sm={6} xs={12}>
                  <FullWidthField
                    format="YYYY-MM-DD"
                    defaultValue={values.appointmentDateInput}
                    type="date"
                    name="appointmentDateInput"
                    label="Appointment Date"
                    inputId="appointmentDateInput"
                    fullWidth
                    clearable
                    shrink
                    required
                    variant="outlined"
                    errorMessage={errors.appointmentDateInput}
                  />
                </Grid>
              </Grid>

              <Grid container spacing={2} style={{ marginTop: '15px' }}>
                <Grid item lg={3} md={6} sm={12} xs={12}></Grid>

                <Grid item lg={3} md={6} sm={12} xs={12}>
                  <MuiButton
                    className="button"
                    fullWidth
                    size="small"
                    onClick={openProviderLookupDialogCallback(setFieldValue, values)}
                    variant="contained"
                    color="secondary"
                  >
                    Select Provider Group & Location
                  </MuiButton>

                  {flattenedProviders.length > 0 && (
                    <>
                      <div className="historical-providers-title">
                        Select From Previous Providers:
                      </div>
                      <div className="historical-providers-container">
                        {flattenedProviders.map((provider, index) => (
                          <div key={index} className="historical-provider">
                            <a
                              href="#"
                              onClick={async (e) => {
                                e.preventDefault()

                                const updates = [
                                  setFieldValue('providerId', provider.providerGroupId),
                                  setFieldValue('provider', provider.providerGroupName),
                                ]

                                await setSelectedProvider!({
                                  id: provider.providerGroupId,
                                  name: provider.providerGroupName,
                                } as IProvider)

                                if (provider.locationName && provider.locationId) {
                                  updates.push(
                                    setFieldValue('locationId', provider.locationName)
                                  )
                                  setSelectedLocation!({
                                    id: provider.locationId,
                                    name: provider.locationName,
                                  } as ILocation)
                                }

                                await Promise.all(updates)

                                resetDuplicateCheckState()

                                if (provider.providerGroupId) {
                                  getAllLocationsForProvider!(provider.providerGroupId)
                                }
                              }}
                              className="provider-link"
                            >
                              {typeof provider.providerGroupName === 'string'
                                ? `${provider.providerGroupName} | ${provider.locationName}, [NPI: ${provider.npi}]`
                                : String(provider.providerGroupName)}
                            </a>
                            &nbsp;&nbsp;
                            <Tooltip
                              title={
                                <span>
                                  {provider?.billingAddress ? (
                                    <>
                                      Billing Address: {provider.billingAddress.name} -{' '}
                                      {provider.billingAddress.fullAddress}
                                      <br />
                                      {provider.billingAddress.city},{' '}
                                      {provider.billingAddress.state}{' '}
                                      {provider.billingAddress.zipCode}
                                    </>
                                  ) : (
                                    'No Billing Address Found'
                                  )}
                                </span>
                              }
                            >
                              <InfoOutlined
                                fontSize="small"
                                style={{
                                  marginRight: '5px',
                                  cursor: 'pointer',
                                  color: '#1976d2',
                                }}
                              />
                            </Tooltip>
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </Grid>
              </Grid>

              {noDuplicatesFound && (
                <Grid container spacing={2} style={{ marginTop: '20px' }}>
                  <Grid item xs={12}>
                    <Typography style={{ color: '#2e7d32', fontWeight: 'bold' }}>
                      ✓ No duplicate claims found. Click Save to continue.
                    </Typography>
                  </Grid>
                </Grid>
              )}

              {duplicateWarning && renderInlineDuplicateWarning()}
            </>
          )
        }}
      </ESDialog>
    </>
  )
})

const InjectedScheduleDialog = inject<
  IStores,
  ISimpleEventDialogProps,
  Partial<ISimpleEventDialogProps>,
  any
>((stores: IStores) => ({
  clearLocations: stores.locations.clearLocations,
  closeModal: stores.schedules.closeEventDialog,
  components: stores.components.components,
  currentAppOrganization: stores.global.currentAppOrganization,
  episodeOfCare: stores.patientEdit.selectedEpisodeOfCare,
  getAllActiveComponents: stores.components.getAllActiveComponents,
  getAllLocationsForProvider: stores.locations.getAllLocationsByProviderId,
  getProviders: stores.providers.getProviderSuggestions,
  getScheduleById: stores.schedules.getScheduleById,
  isLoading: stores.schedules.isLoading,
  isOpen: stores.schedules.isModalOpen,
  isProviderLookupDialogOpen: stores.schedules.isClaimProviderLookupDialogOpen,
  locations: stores.locations.locations,
  openProviderLookupDialog: stores.schedules.openClaimProviderLookupDialog,
  patient: stores.patients.selectedPatient,
  providers: stores.providers.providers,
  saveSchedule: stores.schedules.saveSchedule,
  schedule: stores.schedules.selectedSchedule,
  selectedLocation: stores.locations.selectedEditClaimLocation,
  selectedProvider: stores.providers.selectedModalEditScheduleProvider,
  setSelectedLocation: stores.locations.setSelectedEditClaimLocation,
  setSelectedProvider: stores.providers.setSelectedModalEditScheduleProvider,
  getLocationById: stores.locations.getLocationById,
}))(SimpleEventDialog)

export default InjectedScheduleDialog
